import { useQuery, skipToken, type UseQueryResult } from "@tanstack/react-query";
import { useApiConfig, type ApiConfig } from "@visioni/a3s-client-api/app";
import { expandEndpoint } from "@visioni/a3s-client-api";
import { reportItemByIdQuery } from "@visioni/a3s-reports/app";
import {
  RoadSensePayload,
  safeDecodeRoadSenseListing,
  GetReportResponse,
  ArtifactUrlResponse,
  getReportByIdEndpoint,
  getReportArtifactUrlEndpoint,
  type ReportItem,
  type RoadSenseListingSite,
} from "@visioni/a3s-reports/client-messages";
import { useTenant, useTenantId } from "@src/features/tenants";

/**
 * RoadSense report fetch hook — an **independent** implementation of the same
 * presigned-payload pattern WasteSense uses, deliberately not shared with
 * `features/wastesense`. The only cross-feature code it leans on is the generic
 * query factory (`reportItemByIdQuery`), the endpoint definitions, and the
 * auth/tenant plumbing — all report-agnostic.
 *
 * Reports are fetched by **`reportId`** (a ULID), not `periodStart`: RoadSense
 * reports are cumulative from an asset's hire start, so two reports can share a
 * `periodStart` and differ only in `periodEnd` — `periodStart` is not a unique
 * key for this type. See the identity note in `get-report.ts`.
 *
 * RoadSense is a **multi-file** report: each site has its own render `.json`,
 * not one payload per report. So the fetch is a **three-hop** flow keyed by
 * `(reportId, siteId)`:
 *   1. GET the by-id row → its `listingSummary` carries the per-site list
 *      (`RoadSenseListingSummary`), including each site's opaque `payloadS3Key`.
 *   2. GET the generic artifact-url route with that site's `payloadS3Key` → a
 *      short-lived presigned S3 URL.
 *   3. GET the presigned URL straight from S3 → decode `RoadSensePayload`.
 *
 * (The row's own `payloadUrl` points at the report's `manifest.json`, not a
 * renderable payload, so it is deliberately not followed for RoadSense.)
 *
 * The backend exposes two readiness states the renderer must distinguish:
 *  - `not_found` (404) — no row for `(tenant, "roadsense", reportId)`, or the
 *    row has no entry for `siteId`
 *  - `not_ready` (409) — row exists but `generating` / `failed`
 *
 * We surface them as typed errors so the screen can switch between
 * "still working" and "retry" copy rather than collapsing to a generic
 * message.
 */
export class RoadSenseReportNotFoundError extends Error {
  readonly kind = "not_found" as const;
  constructor(message = "Report not found") {
    super(message);
    this.name = "RoadSenseReportNotFoundError";
  }
}

export class RoadSenseReportNotReadyError extends Error {
  readonly kind = "not_ready" as const;
  readonly status: "generating" | "failed";
  constructor(status: "generating" | "failed", message = "Report not ready") {
    super(message);
    this.name = "RoadSenseReportNotReadyError";
    this.status = status;
  }
}

const STALE_TIME_MS = 60_000;

export interface UseRoadSenseReportResult {
  readonly report: ReportItem | undefined;
  readonly payload: RoadSensePayload | undefined;
  /**
   * This site's `.xlsx` key, read from the SAME row fetch that produced the
   * payload (the site's listing-summary entry carries both keys). Exposed here
   * so the detail screen's download button doesn't need a second by-id fetch —
   * a separate fetch could fail/lag independently and hide the button under a
   * rendered report. `undefined` only when there's no payload yet (no row).
   */
  readonly workbookS3Key: string | undefined;
  readonly isLoading: boolean;
  readonly isError: boolean;
  readonly error: Error | null;
  readonly isTenantPending: boolean;
}

/**
 * Fetches a single RoadSense **site's** report by `(reportId, siteId)` and
 * decodes its plugin-owned payload at the boundary. See the file header for the
 * three-hop flow (row → artifact-url presign → S3 GET → decode).
 */
export function useRoadSenseReport(reportId: string | undefined, siteId: string | undefined): UseRoadSenseReportResult {
  const apiConfig = useApiConfig();
  const tenantId = useTenantId();
  const { isLoading: isTenantLoading } = useTenant();

  // Reuse the by-id endpoint's query key (so cache invalidation lines up with
  // any future writes routed through `mutationFor`) but extend it with the
  // `siteId` — two sites of the same report decode to different payloads, so
  // they must not share a cache entry. The default queryFn from `queryFor` is
  // discarded; we run our own to surface the typed 404/409.
  const keyOptions =
    reportId !== undefined ?
      reportItemByIdQuery(apiConfig, { type: "roadsense", reportId })
    : { queryKey: [] as const };
  const queryKey = reportId !== undefined && siteId !== undefined ? [...keyOptions.queryKey, siteId] : [];

  const enabled = !!tenantId && reportId !== undefined && siteId !== undefined;

  const query = useQuery({
    queryKey,
    queryFn:
      apiConfig.tenant && reportId !== undefined && siteId !== undefined ?
        ({ signal }) => fetchReportWithPayload(apiConfig, reportId, siteId, signal)
      : skipToken,
    enabled,
    staleTime: STALE_TIME_MS,
    refetchOnMount: true,
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
    retry: (failureCount, error) => {
      // 404/409 are terminal for this `(reportId, siteId)`. Other errors keep
      // the default backoff (3 attempts).
      if (error instanceof RoadSenseReportNotFoundError) return false;
      if (error instanceof RoadSenseReportNotReadyError) return false;
      return failureCount < 3;
    },
  }) as UseQueryResult<FetchReportResult, Error>;

  const reportItem = query.data?.response.data;
  const payload = query.data ? decodePayload(query.data.payload) : undefined;

  return {
    report: reportItem,
    payload,
    workbookS3Key: query.data?.workbookS3Key,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error ?? null,
    isTenantPending: isTenantLoading || !tenantId,
  };
}

interface FetchReportResult {
  readonly response: GetReportResponse;
  readonly payload: unknown;
  /** This site's `.xlsx` download key, from the same listing-summary entry. */
  readonly workbookS3Key: string;
}

async function fetchReportWithPayload(
  apiConfig: ApiConfig,
  reportId: string,
  siteId: string,
  signal: AbortSignal | undefined,
): Promise<FetchReportResult> {
  const response = await fetchReport(apiConfig, reportId, signal);
  // The site's render payload + workbook keys both live in the row's
  // listingSummary (not the row's top-level `payloadUrl`, which is the manifest
  // for a multi-file report). Resolve the whole site once so the workbook key
  // rides along on the same fetch — see UseRoadSenseReportResult.workbookS3Key.
  const site = resolveSite(response.data, siteId);
  const presignedUrl = await fetchArtifactUrl(apiConfig, reportId, site.payloadS3Key, signal);
  const payload = await fetchPayload(presignedUrl, signal);
  return { response, payload, workbookS3Key: site.workbookS3Key };
}

/**
 * Pulls the site's listing-summary entry out of the report row's
 * `listingSummary`. A missing site (the URL names a site this report never
 * produced) is a 404, same as a missing report — the renderer shows the same
 * "not found" copy either way.
 */
function resolveSite(row: ReportItem, siteId: string): RoadSenseListingSite {
  // `listingSummary` is an opaque `z.json()` field; the shared decoder owns the
  // one cast and re-validates the structure at runtime.
  const summary = safeDecodeRoadSenseListing(row.listingSummary);
  if (!summary.success) {
    // Drift between the stored listing shape and the schema. Treat as a decode
    // failure surfaced to the user, not a crash.
    console.error("RoadSense listingSummary schema mismatch", summary.error);
    throw new RoadSenseReportNotFoundError("Report listing could not be read");
  }
  const site = summary.data.sites.find((s) => s.siteId === siteId);
  if (!site) {
    throw new RoadSenseReportNotFoundError(`Report has no site '${siteId}'`);
  }
  return site;
}

/**
 * Presigns one per-site artifact via the generic artifact-url route. Used here
 * for the render `.json`; the same route presigns the `.xlsx` download (see
 * `useRoadSenseWorkbookDownload`).
 */
async function fetchArtifactUrl(
  apiConfig: ApiConfig,
  reportId: string,
  key: string,
  signal: AbortSignal | undefined,
): Promise<string> {
  const { tenant, authenticatedFetch } = apiConfig;
  if (!tenant) {
    throw new Error("Tenant not configured");
  }
  const path = expandEndpoint(getReportArtifactUrlEndpoint, {
    path: { tenantId: tenant.tenantId, type: "roadsense", reportId },
    query: { key },
  });
  const url = `${tenant.apiBaseUrl}${path}`;
  const response = await authenticatedFetch(url, signal ? { signal } : {});
  if (response.status === 404) {
    throw new RoadSenseReportNotFoundError();
  }
  if (response.status === 409) {
    const status = await readNotReadyStatus(response);
    throw new RoadSenseReportNotReadyError(status);
  }
  if (!response.ok) {
    const text = await response.text().catch(() => "");
    throw new Error(text || `Artifact-url request failed: ${String(response.status)}`);
  }
  const body: unknown = await response.json();
  return ArtifactUrlResponse.parse(body).data.url;
}

/**
 * Follows the presigned `payloadUrl` with a plain GET. No auth header: the URL
 * is SigV4-presigned, and attaching `Authorization` alongside the query-string
 * signature makes S3 reject it.
 */
async function fetchPayload(payloadUrl: string, signal: AbortSignal | undefined): Promise<unknown> {
  const response = await fetch(payloadUrl, signal ? { signal } : {});
  if (!response.ok) {
    const text = await response.text().catch(() => "");
    throw new Error(text || `Payload fetch failed: ${String(response.status)}`);
  }
  return response.json();
}

async function fetchReport(
  apiConfig: ApiConfig,
  reportId: string,
  signal: AbortSignal | undefined,
): Promise<GetReportResponse> {
  const { tenant, authenticatedFetch } = apiConfig;
  if (!tenant) {
    // Unreachable in practice (gated by `enabled`/`skipToken`); the type system
    // doesn't know that.
    throw new Error("Tenant not configured");
  }
  const path = expandEndpoint(getReportByIdEndpoint, {
    path: { tenantId: tenant.tenantId, type: "roadsense", reportId },
  });
  const url = `${tenant.apiBaseUrl}${path}`;
  const response = await authenticatedFetch(url, signal ? { signal } : {});
  if (response.status === 404) {
    throw new RoadSenseReportNotFoundError();
  }
  if (response.status === 409) {
    const status = await readNotReadyStatus(response);
    throw new RoadSenseReportNotReadyError(status);
  }
  if (!response.ok) {
    const text = await response.text().catch(() => "");
    throw new Error(text || `Request failed: ${String(response.status)}`);
  }
  const body: unknown = await response.json();
  return GetReportResponse.parse(body);
}

async function readNotReadyStatus(response: Response): Promise<"generating" | "failed"> {
  try {
    const body = (await response.json()) as { details?: { status?: unknown } };
    const status = body.details?.status;
    if (status === "generating" || status === "failed") return status;
  } catch {
    // Body wasn't JSON or was missing `details.status`. Default to
    // "generating" — the safer "still working" copy.
  }
  return "generating";
}

function decodePayload(payload: unknown): RoadSensePayload | undefined {
  const result = RoadSensePayload.safeParse(payload);
  if (result.success) return result.data;
  // Backend validates payloads on write, so this only fires on schema drift.
  // Returning `undefined` lets the renderer show an explicit
  // "payload schema mismatch" state rather than crashing.
  console.error("RoadSensePayload schema mismatch", result.error);
  return undefined;
}
